import { useState, useMemo, useEffect } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, rutesDB, anotacionsDB, configDB } from '../services/db'
import { optimitzarRuta, calcularHoresEstimades, haversineDistance } from '../services/routeOptimizer'
import { format } from 'date-fns'
import { ca } from 'date-fns/locale'

const TIPUS = ['peu', 'transport', 'híbrida']
const TIPUS_EMOJI  = { peu: '🚶', transport: '🚌', híbrida: '🔀' }
const TIPUS_COLORS = { peu: '#34d399', transport: '#60a5fa', híbrida: '#f59e0b' }

const avui = () => new Date().toISOString().slice(0, 10)

function generarNomRuta(dataStr) {
  try {
    return format(new Date(dataStr + 'T00:00:00'), "d 'de' MMMM", { locale: ca })
  } catch {
    return ''
  }
}

function tempsEstimat(tipus, n) {
  const min = n * (tipus === 'peu' ? 15 : tipus === 'transport' ? 8 : 10)
  return min > 0 ? `~${min} min` : '—'
}

function calcularDuracioTotal(legs) {
  return (legs ?? []).reduce((acc, leg) => acc + (leg?.mins ?? 0), 0)
}

function calcularDistanciaTotal(legs) {
  return (legs ?? []).reduce((acc, leg) => acc + (leg?.dist ?? 0), 0)
}

export default function RoutesPage({ onViewOnMap, onExecutar }) {
  const [rutes, anotacions, domicilis, config] = useLiveQuery(async () => {
    const [r, a, d, c] = await Promise.all([
      db.rutes.toArray(),
      db.anotacions.toArray(),
      db.domicilis.orderBy('codi').toArray(),
      configDB.getOrDefault(),
    ])
    return [r, a, d, c]
  }, []) ?? [[], [], [], null]

  const comptador = anotacions.reduce((acc, a) => {
    acc[a.ruta_id] = (acc[a.ruta_id] ?? 0) + 1
    return acc
  }, {})

  const [showForm, setShowForm]   = useState(false)
  const [editId, setEditId]       = useState(null)
  const [form, setForm]           = useState(null)
  const [error, setError]         = useState(null)
  const [toast, setToast]         = useState(null)
  const [optimitzant, setOptimitzant]         = useState(false)
  const [simulant, setSimulant]               = useState(false)
  const [simulacio, setSimulacio]             = useState(null)
  const [novaOrdreA, setNovaOrdreA]           = useState('')
  const [novaOrdreB, setNovaOrdreB]           = useState('')
  const [detallRuta, setDetallRuta]           = useState(null)
  const [nomModificat, setNomModificat]       = useState(false)
  const [restriccionsObertes, setRestriccionsObertes] = useState({})
  const [cerca, setCerca]                     = useState('')
  const [estatExecucio, setEstatExecucio]     = useState(null)
  const [mostrarFinalitzades, setMostrarFinalitzades] = useState(false)
  const [diesEliminar, setDiesEliminar]               = useState(30)
  const [mostrarEliminacio, setMostrarEliminacio]     = useState(false)

  // ── Filtre i ordenació ─────────────────────────────────────────────────────
  const rutesFiltradesIOrdenades = useMemo(() => {
    return rutes
      .filter(r =>
        r.nom.toLowerCase().includes(cerca.toLowerCase()) ||
        (r.data ?? '').includes(cerca)
      )
      .sort((a, b) => {
        const dA = new Date(`${a.data ?? ''}T${a.hora_inici ?? '00:00'}`)
        const dB = new Date(`${b.data ?? ''}T${b.hora_inici ?? '00:00'}`)
        return dB - dA
      })
  }, [rutes, cerca])

  const rutesActives = useMemo(() =>
    rutesFiltradesIOrdenades.filter(r => {
      let e = null; try { e = r.estat_json ? JSON.parse(r.estat_json) : null } catch {}
      return !e?.finalitzat
    }), [rutesFiltradesIOrdenades])

  const rutesFinalitzades = useMemo(() =>
    rutesFiltradesIOrdenades.filter(r => {
      let e = null; try { e = r.estat_json ? JSON.parse(r.estat_json) : null } catch {}
      return !!e?.finalitzat
    }), [rutesFiltradesIOrdenades])

  const rutesAEliminar = useMemo(() => {
    const limit = new Date()
    limit.setDate(limit.getDate() - diesEliminar)
    const limitStr = limit.toISOString().slice(0, 10)
    return rutesFinalitzades.filter(r => (r.data ?? '') <= limitStr)
  }, [rutesFinalitzades, diesEliminar])

  function mostrarToast(msg) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  function formBuit() {
    const data = avui()
    return {
      nom:           generarNomRuta(data),
      data,
      hora_inici:    config?.hora_inici ?? '09:00',
      tipus:         config?.tipus_ruta ?? 'peu',
      punt_arribada: 'final',
      domicilis:     [],
      restr_dom:     {},
      ordre:         [],
      legs:          null,
      sequencia:     null,
    }
  }

  function buildRestriccions(restrDom, ordre, allDomicilis) {
    const codiById = Object.fromEntries(allDomicilis.map(d => [d.id, d.codi]))
    const obj = {}
    for (const [domId, restr] of Object.entries(restrDom)) {
      const codi = codiById[Number(domId)]
      if (!codi) continue
      const clean = {}
      if (restr.hora_arribada)                          clean.hora_arribada = restr.hora_arribada
      if (restr.no_abans)                               clean.no_abans      = restr.no_abans
      if (restr.no_despres)                             clean.no_despres    = restr.no_despres
      if (restr.hospital_abans && restr.hospital_abans !== 'cap') {
        clean.hospital_abans = restr.hospital_abans
        if (restr.temps_hospital_abans > 0) clean.temps_hospital_abans = restr.temps_hospital_abans
      }
      if (restr.hospital_despres && restr.hospital_despres !== 'cap') {
        clean.hospital_despres = restr.hospital_despres
        if (restr.temps_hospital_despres > 0) clean.temps_hospital_despres = restr.temps_hospital_despres
      }
      if (restr.es_primer === true)                     clean.es_primer     = true
      if (restr.es_ultim === true)                      clean.es_ultim      = true
      if (Object.keys(clean).length > 0) obj[codi] = clean
    }
    if (ordre.length > 0) obj.ordre = ordre
    return Object.keys(obj).length > 0 ? obj : null
  }

  function obrirNova() {
    setForm(formBuit())
    setEditId(null)
    setError(null)
    setNomModificat(false)
    setRestriccionsObertes({})
    setSimulacio(null)
    setEstatExecucio(null)
    setShowForm(true)
  }

  async function obrirEdicio(ruta) {
    const anots = anotacions
      .filter(a => a.ruta_id === ruta.id)
      .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
    const domIds = anots.map(a => a.domicili_id)

    let restr_dom = {}
    let ordre     = []
    if (ruta.restriccions) {
      try {
        const json = JSON.parse(ruta.restriccions)
        ordre = json.ordre ?? []
        for (const dom of domicilis) {
          if (domIds.includes(dom.id) && json[dom.codi]) {
            const r = json[dom.codi]
            restr_dom[dom.id] = {
              ...r,
              hospital_abans:       r.hospital_abans       ?? r.hospital      ?? 'cap',
              temps_hospital_abans: r.temps_hospital_abans ?? r.temps_hospital ?? null,
              hospital_despres:     r.hospital_despres     ?? 'cap',
              temps_hospital_despres: r.temps_hospital_despres ?? null,
            }
          }
        }
      } catch { /* format antic */ }
    }

    let legs      = null
    let sequencia = null
    let estatExec = null
    if (ruta.legs_json)     { try { legs      = JSON.parse(ruta.legs_json)     } catch {} }
    if (ruta.sequencia_json){ try { sequencia = JSON.parse(ruta.sequencia_json) } catch {} }
    if (ruta.estat_json)    { try { estatExec = JSON.parse(ruta.estat_json)    } catch {} }
    setEstatExecucio(estatExec?.iniciat ? estatExec : null)

    setForm({
      nom:           ruta.nom,
      data:          ruta.data ?? avui(),
      hora_inici:    ruta.hora_inici ?? '09:00',
      tipus:         ruta.tipus ?? 'peu',
      punt_arribada: ruta.punt_arribada ?? 'final',
      domicilis:     domIds,
      restr_dom,
      ordre,
      legs,
      sequencia,
    })
    setEditId(ruta.id)
    setError(null)
    setNomModificat(true)
    setRestriccionsObertes({})
    setSimulacio(null)
    setShowForm(true)
  }

  function setF(key, val) { setForm(f => ({ ...f, [key]: val })) }

  function handleDataChange(novaData) {
    setF('data', novaData)
    if (!nomModificat) setF('nom', generarNomRuta(novaData))
  }

  function handleNomChange(nouNom) {
    setF('nom', nouNom)
    setNomModificat(true)
  }

  function toggleDomicili(domId) {
    setSimulacio(null)
    setForm(f => ({
      ...f,
      sequencia: null,
      domicilis: f.domicilis.includes(domId)
        ? f.domicilis.filter(id => id !== domId)
        : [...f.domicilis, domId],
    }))
  }

  function setRestrDomField(domId, field, val) {
    setForm(f => {
      const restr_dom = { ...f.restr_dom }

      // Exclusivitat: només un domicili pot ser primer i un pot ser últim
      if ((field === 'es_primer' || field === 'es_ultim') && val === true) {
        Object.keys(restr_dom).forEach(id => {
          if (Number(id) !== Number(domId) && restr_dom[id]?.[field]) {
            restr_dom[id] = { ...restr_dom[id], [field]: false }
          }
        })
      }

      return {
        ...f,
        restr_dom: {
          ...restr_dom,
          [domId]: { ...(restr_dom[domId] ?? {}), [field]: val },
        },
      }
    })
  }

  function handleRestriccio(domId, camp, valor) {
    setForm(prev => {
      const novesRestr = { ...prev.restr_dom }

      if (camp === 'es_primer' && valor === true) {
        if (novesRestr[domId]?.es_ultim) {
          console.warn('[Restricció] No pot ser primer i últim')
          return prev
        }
        Object.keys(novesRestr).forEach(c => {
          if (Number(c) !== Number(domId) && novesRestr[c]?.es_primer) {
            novesRestr[c] = { ...novesRestr[c], es_primer: false }
          }
        })
      }

      if (camp === 'es_ultim' && valor === true) {
        if (novesRestr[domId]?.es_primer) {
          console.warn('[Restricció] No pot ser primer i últim')
          return prev
        }
        Object.keys(novesRestr).forEach(c => {
          if (Number(c) !== Number(domId) && novesRestr[c]?.es_ultim) {
            novesRestr[c] = { ...novesRestr[c], es_ultim: false }
          }
        })
      }

      novesRestr[domId] = { ...novesRestr[domId], [camp]: valor }
      return { ...prev, restr_dom: novesRestr }
    })
  }

  function toggleRestricciosDom(domId) {
    setRestriccionsObertes(prev => ({ ...prev, [domId]: !prev[domId] }))
  }

  function addOrdre(codiA, codiB) {
    if (!codiA || !codiB || codiA === codiB) return
    setForm(f => ({
      ...f,
      ordre: [...f.ordre.filter(([a, b]) => !(a === codiA && b === codiB)), [codiA, codiB]],
    }))
  }

  function removeOrdre(codiA, codiB) {
    setForm(f => ({ ...f, ordre: f.ordre.filter(([a, b]) => !(a === codiA && b === codiB)) }))
  }

  // ── Nucli optimitzador (sense efectes secundaris) ──────────────────────────
  async function runOptimizer(currentForm) {
    const seleccionats = currentForm.domicilis.map(id => domicilis.find(d => d.id === id)).filter(Boolean)
    const restriccions = buildRestriccions(currentForm.restr_dom, currentForm.ordre, domicilis)
    const hospital = config?.hospital?.lat ? config.hospital : null
    const { domicilis: ordenats, legs: legsBase, sequencia: seqBase } = await optimitzarRuta(
      seleccionats, currentForm.tipus, currentForm.hora_inici, currentForm.data,
      restriccions, hospital, config?.ubicacio_inici, config
    )
    let legs = legsBase
    const seqCompleta = []
    const inici = config?.ubicacio_inici
    const iniciTéCoords = inici?.lat && inici?.lng
    if (iniciTéCoords && (inici.tipus === 'casa' || inici.tipus === 'custom')) {
      seqCompleta.push({ codi: 'INICI', codi_visita: 'INICI', tipus: 'inici', id: null, nom: inici.adreca || 'Casa', adreca: inici.adreca, lat: inici.lat, lng: inici.lng, dades_json: null })
    } else if (inici?.tipus === 'hospital' && config?.hospital?.lat) {
      seqCompleta.push({ codi: 'INICI', codi_visita: 'INICI', tipus: 'inici', id: null, nom: config.hospital.nom || 'Hospital', adreca: config.hospital.adreca, lat: config.hospital.lat, lng: config.hospital.lng, dades_json: null })
    }
    seqCompleta.push(...(seqBase ?? []))

    // Punt d'arribada opcional
    const puntArribada = currentForm.punt_arribada ?? 'final'
    if (puntArribada !== 'final' && seqCompleta.length > 0) {
      let puntFinal = null
      if (puntArribada === 'hospital' && config?.hospital?.lat) {
        puntFinal = { codi: 'ARRIBADA', codi_visita: 'ARRIBADA_1', tipus: 'arribada', subtipus: 'hospital', nom: config.hospital.nom || 'Hospital', adreca: config.hospital.adreca, lat: config.hospital.lat, lng: config.hospital.lng, dades_json: null }
      } else if (puntArribada === 'casa' && config?.ubicacio_inici?.lat) {
        puntFinal = { codi: 'ARRIBADA', codi_visita: 'ARRIBADA_1', tipus: 'arribada', subtipus: 'casa', nom: 'Casa', adreca: config.ubicacio_inici.adreca, lat: config.ubicacio_inici.lat, lng: config.ubicacio_inici.lng, dades_json: null }
      }
      if (puntFinal) {
        const ultim = seqCompleta[seqCompleta.length - 1]
        const uLat = ultim?.lat ?? ultim?.dades_json?.lat
        const uLng = ultim?.lng ?? ultim?.dades_json?.lng
        if (uLat && uLng) {
          const dist = haversineDistance(uLat, uLng, puntFinal.lat, puntFinal.lng)
          const vel  = currentForm.tipus === 'peu' ? 5000 / 60 : 20000 / 60
          const mins = Math.round(dist / vel)
          legs = [...legs, { tipus: currentForm.tipus === 'peu' ? 'peu' : 'transport', dist, mins, itinerari: null, linia: null, paradaOrigen: null, paradaDesti: null, tempsEspera: null }]
        }
        seqCompleta.push(puntFinal)
      }
    }

    return { seqCompleta, legs, ordenats }
  }

  // ── Simular: optimitza i mostra previsualització, no guarda ───────────────
  async function handleSimular() {
    if (!form || form.domicilis.length < 2) return
    setSimulant(true)
    setError(null)
    try {
      const { seqCompleta, legs, ordenats } = await runOptimizer(form)
      setSimulacio({
        duracio_total:   calcularDuracioTotal(legs),
        distancia_total: calcularDistanciaTotal(legs),
        num_domicilis:   seqCompleta.filter(p => p.tipus === 'domicili').length,
        te_hospital:     seqCompleta.some(p => p.tipus === 'hospital'),
        sequencia:       seqCompleta,
        legs,
        ordenats,
      })
    } catch (e) {
      setError(`Error simulant: ${e.message}`)
    } finally {
      setSimulant(false)
    }
  }

  // ── Optimitzar i Guardar: usa simulació existent o n'executa una nova ──────
  async function handleOptimitzarIGuardar() {
    if (!form || !form.nom.trim()) return setError('El nom és obligatori')
    setError(null)

    let seqCompleta = null, legs = null, ordenats = null

    if (simulacio) {
      seqCompleta = simulacio.sequencia
      legs        = simulacio.legs
      ordenats    = simulacio.ordenats
    } else if (form.domicilis.length >= 2) {
      setOptimitzant(true)
      try {
        const result = await runOptimizer(form)
        seqCompleta = result.seqCompleta
        legs        = result.legs
        ordenats    = result.ordenats
      } catch (e) {
        setError(`Error optimitzant: ${e.message}`)
        setOptimitzant(false)
        return
      } finally {
        setOptimitzant(false)
      }
    }

    try {
      // Calcula hora fi prevista (hora de sortida de l'últim punt)
      const restriccionsObj = buildRestriccions(form.restr_dom, form.ordre, domicilis)
      let horaFiPrevista = ''
      if (seqCompleta?.length && legs) {
        try {
          const hores = calcularHoresEstimades(seqCompleta, legs, form.hora_inici, config, restriccionsObj)
          const idx   = seqCompleta.length - 1
          const ultim = seqCompleta[idx]
          const clau  = ultim?.codi_visita || `${ultim?.codi}_${idx}`
          horaFiPrevista = hores?.[clau]?.hora_sortida ?? ''
        } catch { /* no bloqueja el guardat */ }
      }

      const dadesRuta = {
        nom:              form.nom.trim(),
        data:             form.data,
        hora_inici:       form.hora_inici,
        tipus:            form.tipus,
        punt_arribada:    form.punt_arribada ?? 'final',
        restriccions:     restriccionsObj ? JSON.stringify(restriccionsObj) : '',
        legs_json:        legs ? JSON.stringify(legs) : '',
        sequencia_json:   seqCompleta?.length ? JSON.stringify(seqCompleta) : '',
        hora_fi_prevista: horaFiPrevista,
      }

      let rutaId
      if (editId) {
        await rutesDB.update(editId, dadesRuta)
        await anotacionsDB.deleteByRuta(editId)
        rutaId = editId
        mostrarToast('Ruta actualitzada')
      } else {
        rutaId = await rutesDB.create(dadesRuta)
        mostrarToast('Ruta creada')
      }

      const domIdsOrdenats = ordenats
        ? ordenats.filter(d => !d.esHospital && d.id != null).map(d => d.id)
        : form.domicilis
      for (let i = 0; i < domIdsOrdenats.length; i++) {
        const domId = domIdsOrdenats[i]
        if (domId == null) continue
        await anotacionsDB.create({ ruta_id: rutaId, domicili_id: domId, ordre: i, restriccions: '' })
      }

      setShowForm(false)
    } catch (e) {
      setError(e.message)
    }
  }

  async function reiniciarExecucio() {
    if (!editId) return
    if (!confirm('Segur que vols reiniciar l\'execució? Es perdran totes les dades de visita registrades.')) return
    await rutesDB.update(editId, { estat_json: '' })
    setEstatExecucio(null)
    mostrarToast('Execució reiniciada')
  }

  async function eliminar(ruta) {
    if (!confirm(`Eliminar la ruta "${ruta.nom}"?`)) return
    await rutesDB.delete(ruta.id)
    mostrarToast('Ruta eliminada')
  }

  async function eliminarFinalitzades() {
    for (const ruta of rutesAEliminar) {
      await rutesDB.delete(ruta.id)
    }
    setMostrarEliminacio(false)
    mostrarToast(`${rutesAEliminar.length} rutes eliminades`)
  }

  const hospitalConfigurat = !!(config?.hospital?.lat)

  useEffect(() => {
    if (!config || !domicilis.length) return
    const numSlots = config.num_slots ?? 40
    const foraRang = domicilis.filter(d => {
      const num = parseInt(d.codi.replace('HADO', ''))
      return !isNaN(num) && num > numSlots
    })
    if (foraRang.length > 0) {
      console.warn('[Config] Domicilis fora de rang:', foraRang.map(d => d.codi))
    }
  }, [config, domicilis])

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>Rutes</h1>
        <button style={S.btnAdd} onClick={obrirNova}>+</button>
      </header>

      {/* Cercador */}
      <div style={S.cercaWrap}>
        <span style={S.cercaIcon}>🔍</span>
        <input
          style={S.cercaInput}
          placeholder="Cerca per nom o data…"
          value={cerca}
          onChange={e => setCerca(e.target.value)}
        />
        {cerca && (
          <button style={S.cercaClear} onClick={() => setCerca('')}>✕</button>
        )}
      </div>

      <div style={S.llista}>
        {rutesActives.length === 0 && rutesFinalitzades.length === 0 && (
          <div style={S.buit}>
            <p style={S.buitTitol}>{cerca ? 'Cap resultat' : 'Cap ruta creada'}</p>
            <p style={S.buitHint}>{cerca ? 'Prova amb un altre terme' : 'Prem + per generar la primera ruta'}</p>
          </div>
        )}
        {rutesActives.map((ruta, ri) => (
          <RutaItem key={ruta.id ?? ri} ruta={ruta} comptador={comptador}
            onDetall={() => setDetallRuta(ruta)}
            onViewOnMap={onViewOnMap}
            onExecutar={onExecutar}
            onEditar={obrirEdicio}
            onEliminar={eliminar}
          />
        ))}

        {rutesFinalitzades.length > 0 && (
          <>
            <div style={S.seccioFinalitzades} onClick={() => setMostrarFinalitzades(v => !v)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13, color: '#6b7280' }}>
                <span>{mostrarFinalitzades ? '▼' : '▶'}</span>
                <span>Rutes finalitzades</span>
                <span style={S.badgeCount}>{rutesFinalitzades.length}</span>
              </span>
              <button style={S.btnNetejar}
                onClick={e => { e.stopPropagation(); setMostrarEliminacio(true) }}>
                🗑️ Netejar
              </button>
            </div>
            {mostrarFinalitzades && rutesFinalitzades.map((ruta, ri) => (
              <RutaItem key={ruta.id ?? ri} ruta={ruta} comptador={comptador}
                onDetall={() => setDetallRuta(ruta)}
                onViewOnMap={onViewOnMap}
                onExecutar={onExecutar}
                onEditar={obrirEdicio}
                onEliminar={eliminar}
                finalitzada
              />
            ))}
          </>
        )}
      </div>

      {/* Modal: Formulari ruta */}
      {showForm && form && (
        <Overlay onClose={() => setShowForm(false)}>
          <h2 style={S.modalTitol}>{editId ? 'Editar ruta' : 'Nova ruta'}</h2>

          {/* Bloc reinici — visible només si la ruta ja s'ha iniciat */}
          {editId && estatExecucio && (
            <div style={S.reiniciarBloc}>
              <div style={S.reiniciarInfo}>
                <span style={{ fontSize: 20 }}>
                  {estatExecucio.finalitzat ? '🏁' : '▶'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={S.reiniciarTitol}>
                    {estatExecucio.finalitzat ? 'Ruta completada' : 'Ruta en curs'}
                  </p>
                  <p style={S.reiniciarDesc}>
                    {estatExecucio.hora_inici_real && `Inici: ${estatExecucio.hora_inici_real}`}
                    {estatExecucio.finalitzat && estatExecucio.hora_fi_real && ` · Fi: ${estatExecucio.hora_fi_real}`}
                    {!estatExecucio.finalitzat && (() => {
                      const estats = Object.values(estatExecucio.domicilis_estat ?? {})
                      const nFets  = estats.filter(e => e.estat === 'realitzat').length
                      const nTot   = estats.length
                      return nTot ? ` · ${nFets}/${nTot} visites` : ''
                    })()}
                  </p>
                </div>
              </div>
              <button style={S.btnReiniciar} onClick={reiniciarExecucio}>
                🔄 Reiniciar execució
              </button>
            </div>
          )}

          {/* Data i hora primer */}
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Data</label>
              <input style={S.input} type="date"
                value={form.data}
                onChange={e => handleDataChange(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={S.label}>Hora inici</label>
              <input style={S.input} type="time"
                value={form.hora_inici}
                onChange={e => setF('hora_inici', e.target.value)} />
            </div>
          </div>

          {/* Nom auto-generat però editable */}
          <label style={S.label}>Nom de la ruta</label>
          <input
            style={S.input}
            placeholder="ex: 19 de maig"
            value={form.nom}
            onChange={e => handleNomChange(e.target.value)}
          />

          <label style={S.label}>Tipus</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {TIPUS.map(t => (
              <button key={t}
                style={{ ...S.tipusBtn, backgroundColor: form.tipus === t ? '#007AFF' : '#f3f4f6', color: form.tipus === t ? '#fff' : '#374151' }}
                onClick={() => { setF('tipus', t); setSimulacio(null) }}>
                {TIPUS_EMOJI[t]} {t}
              </button>
            ))}
          </div>

          <label style={S.label}>Punt d'arribada</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { val: 'final',    icon: '🏁', label: 'Final',    disabled: false },
              { val: 'hospital', icon: '🏥', label: 'Hospital', disabled: !hospitalConfigurat },
              { val: 'casa',     icon: '🏡', label: 'Casa',     disabled: !config?.ubicacio_inici?.lat },
            ].map(opt => (
              <button key={opt.val}
                style={{ ...S.tipusBtn, flex: 1, backgroundColor: (form.punt_arribada ?? 'final') === opt.val ? '#007AFF' : '#f3f4f6', color: (form.punt_arribada ?? 'final') === opt.val ? '#fff' : opt.disabled ? '#bbb' : '#374151', opacity: opt.disabled ? 0.5 : 1 }}
                onClick={() => !opt.disabled && setF('punt_arribada', opt.val)}
                disabled={opt.disabled}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>

          {/* Selecció de domicilis */}
          <label style={{ ...S.label, marginTop: 14 }}>
            Domicilis ({form.domicilis.length} sel.)
          </label>

          {/* Seqüència optimitzada / simulada */}
          {(simulacio?.sequencia ?? form.sequencia) && (
            <div style={S.sequenciaBloc}>
              {(simulacio?.sequencia ?? form.sequencia).map((punt, i) => (
                <span key={`seq-${i}`} style={
                  punt.tipus === 'hospital' ? S.seqHospital :
                  punt.tipus === 'inici'   ? S.seqInici    : S.seqDom
                }>
                  {i > 0 && <span style={S.seqArrow}>→</span>}
                  {punt.tipus === 'hospital' ? '🏥' : punt.tipus === 'inici' ? '📍' : `${i + 1}.`} {punt.codi}
                </span>
              ))}
            </div>
          )}

          {domicilis.length === 0 && (
            <p style={S.hint}>No hi ha domicilis. Afegeix-ne a la pestanya 🏠.</p>
          )}
          <div style={S.domGrid}>
            {domicilis.map(dom => {
              const sel = form.domicilis.includes(dom.id)
              const seq = simulacio?.sequencia ?? form.sequencia
              const pos = sel
                ? (seq
                    ? seq.filter(d => d.tipus === 'domicili').findIndex(d => d.id === dom.id) + 1
                    : form.domicilis.indexOf(dom.id) + 1)
                : null
              return (
                <button key={dom.id}
                  style={{ ...S.domBtn, backgroundColor: sel ? '#007AFF' : '#f3f4f6', color: sel ? '#fff' : '#374151', borderColor: sel ? '#007AFF' : '#e5e7eb' }}
                  onClick={() => toggleDomicili(dom.id)}>
                  {sel && <span style={S.domPos}>{pos}</span>}
                  {dom.codi}
                  {sel && form.restr_dom?.[dom.id]?.es_primer && <span style={S.badgePrimer}>1r</span>}
                  {sel && form.restr_dom?.[dom.id]?.es_ultim  && <span style={S.badgeUltim}>Últ</span>}
                </button>
              )
            })}
          </div>

          {/* Restriccions per domicili — col·lapsades individualment */}
          {form.domicilis.length > 0 && (
            <>
              <div style={S.seccioSep}>
                <div style={S.seccioLinia} />
                <span style={S.seccioLabel}>Restriccions per domicili</span>
                <div style={S.seccioLinia} />
              </div>
              {form.domicilis.filter(id => id != null).map((domId, i) => {
                const dom   = domicilis.find(d => d.id === domId)
                const restr = form.restr_dom[domId] ?? {}
                const obert = !!restriccionsObertes[domId]
                return (
                  <div key={domId} style={S.restrDomBloc}>
                    <button style={S.restrDomHeader} onClick={() => toggleRestricciosDom(domId)}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={S.restrDomCodi}>{i + 1}. {dom?.codi}</span>
                        {restr.es_primer && <span style={S.badgePrimer}>1r</span>}
                        {restr.es_ultim  && <span style={S.badgeUltim}>Últ</span>}
                      </span>
                      <span style={S.restrDomToggle}>
                        {obert ? '▲ Amagar' : '▼ + Restriccions'}
                      </span>
                    </button>
                    {obert && (
                      <div style={{ paddingTop: 8 }}>
                        <div style={S.restrGrid}>
                          <span style={S.restrLabel}>⏰ Hora d'arribada</span>
                          <input type="time" style={S.restrInput}
                            value={restr.hora_arribada ?? ''}
                            onChange={e => setRestrDomField(domId, 'hora_arribada', e.target.value)} />
                          <span style={S.restrLabel}>🕐 No abans de</span>
                          <input type="time" style={S.restrInput}
                            value={restr.no_abans ?? ''}
                            onChange={e => setRestrDomField(domId, 'no_abans', e.target.value)} />
                          <span style={S.restrLabel}>🕑 No després de</span>
                          <input type="time" style={S.restrInput}
                            value={restr.no_despres ?? ''}
                            onChange={e => setRestrDomField(domId, 'no_despres', e.target.value)} />
                        </div>

                        {/* Restricció hospital — Abans */}
                        <div style={S.restrHospBloc}>
                          <span style={{ ...S.restrLabel, fontWeight: 600 }}>
                            🏥 Abans d'aquest domicili
                            {!hospitalConfigurat && <span style={S.restrHospWarn}> (configura'l a Ajustos)</span>}
                          </span>
                          <div style={S.restrHospOpcions}>
                            {[
                              { val: 'cap',        label: 'Cap restricció' },
                              { val: 'abans',      label: 'Hospital en algun moment abans' },
                              { val: 'just_abans', label: 'Hospital just abans' },
                            ].map(op => (
                              <label key={op.val} style={S.restrHospOpcio}>
                                <input type="radio"
                                  name={`hospital_abans-${domId}`}
                                  value={op.val}
                                  checked={(restr.hospital_abans ?? 'cap') === op.val}
                                  onChange={() => setRestrDomField(domId, 'hospital_abans', op.val)}
                                  disabled={!hospitalConfigurat && op.val !== 'cap'}
                                />
                                <span>{op.label}</span>
                              </label>
                            ))}
                          </div>
                          {restr.hospital_abans && restr.hospital_abans !== 'cap' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                              <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>⏱️ Temps:</span>
                              <input
                                type="number"
                                min={5} max={240} step={5}
                                value={restr.temps_hospital_abans ?? config?.temps_atencio?.normal ?? 30}
                                onChange={e => setRestrDomField(domId, 'temps_hospital_abans', Math.max(5, parseInt(e.target.value) || 5))}
                                style={{ width: 64, padding: '5px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 14, textAlign: 'center', backgroundColor: '#fff' }}
                              />
                              <span style={{ fontSize: 12, color: '#6b7280' }}>min</span>
                            </div>
                          )}
                        </div>

                        {/* Restricció hospital — Després */}
                        <div style={{ ...S.restrHospBloc, marginTop: 6 }}>
                          <span style={{ ...S.restrLabel, fontWeight: 600 }}>
                            🏥 Després d'aquest domicili
                            {!hospitalConfigurat && <span style={S.restrHospWarn}> (configura'l a Ajustos)</span>}
                          </span>
                          <div style={S.restrHospOpcions}>
                            {[
                              { val: 'cap',          label: 'Cap restricció' },
                              { val: 'despres',      label: 'Hospital en algun moment després' },
                              { val: 'just_despres', label: 'Hospital just després' },
                            ].map(op => (
                              <label key={op.val} style={S.restrHospOpcio}>
                                <input type="radio"
                                  name={`hospital_despres-${domId}`}
                                  value={op.val}
                                  checked={(restr.hospital_despres ?? 'cap') === op.val}
                                  onChange={() => setRestrDomField(domId, 'hospital_despres', op.val)}
                                  disabled={!hospitalConfigurat && op.val !== 'cap'}
                                />
                                <span>{op.label}</span>
                              </label>
                            ))}
                          </div>
                          {restr.hospital_despres && restr.hospital_despres !== 'cap' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                              <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>⏱️ Temps:</span>
                              <input
                                type="number"
                                min={5} max={240} step={5}
                                value={restr.temps_hospital_despres ?? config?.temps_atencio?.normal ?? 30}
                                onChange={e => setRestrDomField(domId, 'temps_hospital_despres', Math.max(5, parseInt(e.target.value) || 5))}
                                style={{ width: 64, padding: '5px 8px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 14, textAlign: 'center', backgroundColor: '#fff' }}
                              />
                              <span style={{ fontSize: 12, color: '#6b7280' }}>min</span>
                            </div>
                          )}
                        </div>

                        {/* Posició a la ruta */}
                        {(() => {
                          const esPrimer    = restr.es_primer === true
                          const esUltim     = restr.es_ultim  === true
                          const altrePrimer = Object.entries(form.restr_dom || {}).some(([c, r]) => Number(c) !== domId && r?.es_primer)
                          const altreUltim  = Object.entries(form.restr_dom || {}).some(([c, r]) => Number(c) !== domId && r?.es_ultim)
                          return (
                            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 4, marginTop: 4 }}>
                              <Switch
                                label="📍 Primer domicili de la ruta"
                                value={esPrimer}
                                onChange={val => handleRestriccio(domId, 'es_primer', val)}
                                disabled={esUltim || (!esPrimer && altrePrimer)}
                              />
                              <Switch
                                label="🏁 Últim domicili de la ruta"
                                value={esUltim}
                                onChange={val => handleRestriccio(domId, 'es_ultim', val)}
                                disabled={esPrimer || (!esUltim && altreUltim)}
                              />
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}

          {/* Ordre parcial */}
          {form.domicilis.length > 1 && (
            <>
              <div style={S.seccioSep}>
                <div style={S.seccioLinia} />
                <span style={S.seccioLabel}>Ordre parcial</span>
                <div style={S.seccioLinia} />
              </div>
              {form.ordre.map(([a, b]) => (
                <div key={`${a}-${b}`} style={S.ordreFila}>
                  <span style={S.ordreCodi}>{a}</span>
                  <span style={S.ordreArrow}>→ abans que →</span>
                  <span style={S.ordreCodi}>{b}</span>
                  <button style={S.ordreDelBtn} onClick={() => removeOrdre(a, b)}>✕</button>
                </div>
              ))}
              <div style={S.ordreAfegir}>
                <select style={S.ordreSelect} value={novaOrdreA} onChange={e => setNovaOrdreA(e.target.value)}>
                  <option value="">— A —</option>
                  {form.domicilis.map(id => {
                    const dom = domicilis.find(d => d.id === id)
                    return <option key={id} value={dom?.codi}>{dom?.codi}</option>
                  })}
                </select>
                <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>→ abans que</span>
                <select style={S.ordreSelect} value={novaOrdreB} onChange={e => setNovaOrdreB(e.target.value)}>
                  <option value="">— B —</option>
                  {form.domicilis.map(id => {
                    const dom = domicilis.find(d => d.id === id)
                    return <option key={id} value={dom?.codi}>{dom?.codi}</option>
                  })}
                </select>
                <button
                  style={{ ...S.ordreAddBtn, opacity: (!novaOrdreA || !novaOrdreB || novaOrdreA === novaOrdreB) ? 0.4 : 1 }}
                  disabled={!novaOrdreA || !novaOrdreB || novaOrdreA === novaOrdreB}
                  onClick={() => { addOrdre(novaOrdreA, novaOrdreB); setNovaOrdreA(''); setNovaOrdreB('') }}>
                  + Afegir
                </button>
              </div>
            </>
          )}

          {/* Previsualització de simulació */}
          {simulacio && (
            <div style={S.simulacioBloc}>
              <div style={S.simulacioHeader}>
                <span style={S.simulacioTitol}>Previsualització de ruta</span>
                <button style={S.simulacioClose} onClick={() => setSimulacio(null)}>✕</button>
              </div>
              <div style={S.simulacioStats}>
                <div style={S.simulacioStat}>
                  <span style={S.simulacioStatVal}>{simulacio.num_domicilis}</span>
                  <span style={S.simulacioStatLabel}>domicilis</span>
                </div>
                <div style={S.simulacioStat}>
                  <span style={S.simulacioStatVal}>{simulacio.duracio_total}</span>
                  <span style={S.simulacioStatLabel}>min totals</span>
                </div>
                <div style={S.simulacioStat}>
                  <span style={S.simulacioStatVal}>{(simulacio.distancia_total / 1000).toFixed(1)}</span>
                  <span style={S.simulacioStatLabel}>km</span>
                </div>
                {simulacio.te_hospital && (
                  <div style={S.simulacioStat}>
                    <span style={S.simulacioStatVal}>🏥</span>
                    <span style={S.simulacioStatLabel}>hospital</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && <p style={S.error}>{error}</p>}

          {/* Botons d'acció */}
          <div style={S.botomsAccio}>
            <button
              style={{ ...S.btnSimular, opacity: (simulant || form.domicilis.length < 2) ? 0.5 : 1 }}
              onClick={handleSimular}
              disabled={simulant || form.domicilis.length < 2}>
              {simulant ? '⏳ Simulant…' : '👁️ Simular'}
            </button>
            <button
              style={{ ...S.btnOptimitzarIGuardar, opacity: optimitzant ? 0.6 : 1 }}
              onClick={handleOptimitzarIGuardar}
              disabled={optimitzant}>
              {optimitzant ? '⏳ Optimitzant…' : simulacio ? '✅ Guardar ruta' : '✅ Optimitzar i Guardar'}
            </button>
          </div>
        </Overlay>
      )}

      {mostrarEliminacio && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 20, maxWidth: 380, width: '100%', maxHeight: '80dvh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: '#111827' }}>🗑️ Netejar rutes finalitzades</h3>
            <p style={{ fontSize: 14, color: '#374151', margin: '0 0 10px' }}>Eliminar rutes completades fa més de:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <input
                type="number" min={1} max={365}
                value={diesEliminar}
                onChange={e => setDiesEliminar(Math.max(1, parseInt(e.target.value) || 1))}
                style={{ width: 70, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 15, textAlign: 'center' }}
              />
              <span style={{ fontSize: 14, color: '#374151' }}>dies</span>
            </div>
            {rutesAEliminar.length > 0 ? (
              <>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 6px' }}>S'eliminaran {rutesAEliminar.length} rutes:</p>
                <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
                  {rutesAEliminar.map(r => (
                    <div key={r.id} style={{ fontSize: 13, color: '#374151', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                      {r.nom} <span style={{ color: '#9ca3af' }}>· {r.data}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 14 }}>Cap ruta compleix el criteri.</p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{ flex: 1, backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, cursor: 'pointer' }}
                onClick={() => setMostrarEliminacio(false)}>
                Cancel·lar
              </button>
              <button
                style={{ flex: 1, backgroundColor: rutesAEliminar.length > 0 ? '#ef4444' : '#e5e7eb', color: rutesAEliminar.length > 0 ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8, padding: '11px', fontSize: 14, fontWeight: 600, cursor: rutesAEliminar.length > 0 ? 'pointer' : 'not-allowed' }}
                disabled={rutesAEliminar.length === 0}
                onClick={eliminarFinalitzades}>
                Eliminar {rutesAEliminar.length > 0 ? `${rutesAEliminar.length} rutes` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {detallRuta && (
        <RutaDetallModal
          ruta={detallRuta}
          anotacions={anotacions}
          domicilis={domicilis}
          onClose={() => setDetallRuta(null)}
          onViewOnMap={id => { onViewOnMap?.(id); setDetallRuta(null) }}
          onEdit={ruta => { obrirEdicio(ruta); setDetallRuta(null) }}
          onExecutar={id => { setDetallRuta(null); onExecutar?.(id) }}
        />
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  )
}

const COLORS_DOM = ['#007AFF','#ff3b30','#ff9500','#34c759','#af52de','#5ac8fa','#ffcc00','#ff2d55']

function RutaItem({ ruta, comptador, onDetall, onViewOnMap, onExecutar, onEditar, onEliminar, finalitzada }) {
  const n = comptador[ruta.id] ?? 0
  let estatRuta = null
  if (ruta.estat_json) { try { estatRuta = JSON.parse(ruta.estat_json) } catch {} }
  const enCurs = estatRuta?.iniciat && !estatRuta?.finalitzat

  return (
    <div style={{ ...S.card, ...(finalitzada ? { opacity: 0.7 } : {}) }} onClick={onDetall}>
      <div style={S.cardTop}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={S.nomRuta}>{ruta.nom}</span>
          {ruta.data && (
            <span style={S.dataRuta}>
              📅 {format(new Date(ruta.data + 'T00:00:00'), "d MMM yyyy", { locale: ca })}
              {ruta.hora_inici && ` · ${ruta.hora_inici}`}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <span style={{ ...S.badge, backgroundColor: TIPUS_COLORS[ruta.tipus ?? 'peu'] }}>
            {TIPUS_EMOJI[ruta.tipus ?? 'peu']} {ruta.tipus ?? 'peu'}
          </span>
          {enCurs      && <span style={S.badgeEnCurs}>● En curs</span>}
          {finalitzada && <span style={S.badgeFinalitzada}>● Completada</span>}
        </div>
      </div>
      <div style={S.cardMeta}>
        <span>{n} domicili{n !== 1 ? 's' : ''}</span>
        {n > 0 && <span>{tempsEstimat(ruta.tipus, n)}</span>}
      </div>
      <div style={S.cardBtns}>
        <button
          style={{ ...S.btnExecutar, flex: 2, ...(finalitzada ? { backgroundColor: '#6b7280' } : {}) }}
          onClick={e => { e.stopPropagation(); onExecutar?.(ruta.id) }}
        >
          {finalitzada ? '👁️ Veure' : enCurs ? '▶ Continuar' : '▶ Iniciar'}
        </button>
        <button style={S.btnMapa} onClick={e => { e.stopPropagation(); onViewOnMap?.(ruta.id) }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <span style={{ fontSize: 15 }}>🗺</span>
            <span style={{ fontSize: 9, lineHeight: 1 }}>Mapa</span>
          </div>
        </button>
        <button style={S.btnEdit} onClick={e => { e.stopPropagation(); onEditar(ruta) }}>✎</button>
        <button style={S.btnDel}  onClick={e => { e.stopPropagation(); onEliminar(ruta) }}>✕</button>
      </div>
    </div>
  )
}

function RutaDetallModal({ ruta, anotacions, domicilis, onClose, onViewOnMap, onEdit, onExecutar }) {
  const doms = anotacions
    .filter(a => a.ruta_id === ruta.id)
    .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
    .map(a => domicilis.find(d => d.id === a.domicili_id))
    .filter(Boolean)

  return (
    <Overlay onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 }}>
        <h2 style={S.modalTitol}>{ruta.nom}</h2>
        <span style={{ ...S.badge, backgroundColor: TIPUS_COLORS[ruta.tipus ?? 'peu'], marginTop: 2 }}>
          {TIPUS_EMOJI[ruta.tipus ?? 'peu']} {ruta.tipus ?? 'peu'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 12, fontSize: 13, color: '#6b7280', marginBottom: 14, flexWrap: 'wrap' }}>
        {ruta.data && <span>{format(new Date(ruta.data + 'T00:00:00'), "d MMM yyyy", { locale: ca })}</span>}
        {ruta.hora_inici && <span>⏰ {ruta.hora_inici}</span>}
        <span>{doms.length} domicili{doms.length !== 1 ? 's' : ''}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 16 }}>
        {doms.length === 0 && <p style={S.hint}>Cap domicili assignat a aquesta ruta</p>}
        {doms.map((dom, i) => (
          <div key={dom.id ?? i} style={S.detallDomFila}>
            <div style={{ ...S.detallDomNum, backgroundColor: COLORS_DOM[i % COLORS_DOM.length] }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#111827', display: 'block' }}>{dom.codi}</span>
              {dom.adreça && <span style={{ fontSize: 12, color: '#6b7280', display: 'block' }}>{dom.adreça}</span>}
              {dom.dades_json?.persona_nom && (
                <span style={{ fontSize: 13, color: '#374151', display: 'block', marginTop: 1 }}>👤 {dom.dades_json.persona_nom}</span>
              )}
              {dom.dades_json?.telefon && (
                <a href={`tel:${dom.dades_json.telefon.replace(/\s/g, '')}`}
                   style={{ fontSize: 13, color: '#1d4ed8', display: 'block', textDecoration: 'none', marginTop: 1 }}>
                  📞 {dom.dades_json.telefon}
                </a>
              )}
              {dom.dades_json?.estat === 'ingres' && <span style={S.detallBadgeIngres}>🟢 NOU INGRÉS</span>}
              {dom.dades_json?.estat === 'alta'   && <span style={S.detallBadgeAlta}>🔴 ALTA</span>}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={S.btnExecutar} onClick={() => onExecutar?.(ruta.id)}>▶ Iniciar</button>
        <button style={S.btnMapa} onClick={() => onViewOnMap(ruta.id)}>🗺 Mapa</button>
        <button style={{ ...S.btnEdit, display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', fontSize: 13 }}
          onClick={() => onEdit(ruta)}>✎ Editar</button>
      </div>
    </Overlay>
  )
}

function Overlay({ children, onClose }) {
  return (
    <div style={S.overlayFons} onClick={onClose}>
      <div style={S.overlayContingut} onClick={e => e.stopPropagation()}>
        <button style={S.closeBtn} onClick={onClose}>✕</button>
        {children}
      </div>
    </div>
  )
}

function Switch({ value, onChange, label, disabled }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0', cursor: disabled ? 'not-allowed' : 'pointer' }}
      onClick={() => !disabled && onChange(!value)}
    >
      {label && <span style={{ fontSize: 13, color: disabled ? '#ccc' : '#374151' }}>{label}</span>}
      <div style={{
        position: 'relative', width: 44, height: 26, borderRadius: 13,
        background: value ? '#34C759' : '#ddd',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s', flexShrink: 0, opacity: disabled ? 0.5 : 1,
      }}>
        <div style={{
          position: 'absolute', top: 3, left: value ? 21 : 3, width: 20, height: 20,
          borderRadius: '50%', background: 'white',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.2s',
        }} />
      </div>
    </div>
  )
}

const S = {
  page:    { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#f9fafb' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 10px', backgroundColor: '#fff', borderBottom: '1px solid #e5e7eb' },
  title:   { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
  btnAdd:  { backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  // ── Cerca ────────────────────────────────────────────────────────────────────
  cercaWrap:  { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', backgroundColor: '#fff', borderBottom: '1px solid #f3f4f6' },
  cercaIcon:  { fontSize: 15, color: '#9ca3af', flexShrink: 0 },
  cercaInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#111827', backgroundColor: 'transparent' },
  cercaClear: { background: 'none', border: 'none', fontSize: 14, color: '#9ca3af', cursor: 'pointer', padding: '0 2px' },

  llista:  { flex: 1, overflowY: 'auto', padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 10 },
  buit:    { textAlign: 'center', marginTop: 60 },
  buitTitol: { fontSize: 16, fontWeight: 600, color: '#6b7280', margin: '0 0 4px' },
  buitHint:  { fontSize: 14, color: '#9ca3af', margin: 0 },
  card:     { backgroundColor: '#fff', borderRadius: 12, padding: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', cursor: 'pointer' },
  cardTop:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 },
  nomRuta:  { fontSize: 16, fontWeight: 600, color: '#111827', display: 'block' },
  dataRuta: { fontSize: 12, color: '#9ca3af', display: 'block', marginTop: 2 },
  badge:    { fontSize: 11, color: '#fff', padding: '3px 8px', borderRadius: 999, fontWeight: 500, whiteSpace: 'nowrap' },
  cardMeta: { display: 'flex', gap: 12, fontSize: 13, color: '#6b7280', marginBottom: 4, flexWrap: 'wrap' },
  restriccions: { margin: '4px 0 8px', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' },
  cardBtns: { display: 'flex', gap: 6, marginTop: 10 },
  btnExecutar: { flex: 2, backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnMapa:  { flex: 1, backgroundColor: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnEdit:  { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: '#6b7280', fontSize: 14 },
  btnDel:   { backgroundColor: '#fff5f5', border: '1px solid #fee2e2', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', color: '#ef4444', fontSize: 12 },

  overlayFons:      { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' },
  overlayContingut: { backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '20px 20px 32px', width: '100%', maxHeight: '92dvh', overflowY: 'auto', position: 'relative' },
  closeBtn:         { position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 18, color: '#9ca3af', cursor: 'pointer', padding: 4 },
  modalTitol:       { margin: '0 0 14px', fontSize: 18, fontWeight: 700, color: '#111827' },
  label:  { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4, marginTop: 12 },
  input:  { width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box', backgroundColor: '#fafafa' },
  hint:   { fontSize: 12, color: '#9ca3af', margin: '4px 0 0' },
  error:  { margin: '8px 0 0', fontSize: 13, color: '#ef4444' },
  tipusBtn:   { flex: 1, border: '1px solid transparent', borderRadius: 8, padding: '8px 4px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  domGrid:    { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  domBtn:     { border: '1px solid', borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', gap: 4 },
  domPos:     { backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 999, fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '16px' },
  badgePrimer:    { fontSize: 9,  fontWeight: 700, color: '#fff', backgroundColor: 'rgba(52,199,89,0.9)',  borderRadius: 999, padding: '1px 4px',  lineHeight: '14px' },
  badgeUltim:     { fontSize: 9,  fontWeight: 700, color: '#fff', backgroundColor: 'rgba(255,59,48,0.9)', borderRadius: 999, padding: '1px 4px',  lineHeight: '14px' },
  badgeEnCurs:    { fontSize: 10, fontWeight: 600, color: '#166534', backgroundColor: '#dcfce7', borderRadius: 999, padding: '2px 7px' },
  badgeFinalitzada: { fontSize: 10, fontWeight: 600, color: '#374151', backgroundColor: '#f3f4f6', borderRadius: 999, padding: '2px 7px' },

  // ── Seccions separadores ─────────────────────────────────────────────────────
  seccioSep:   { display: 'flex', alignItems: 'center', gap: 8, margin: '16px 0 8px' },
  seccioLinia: { flex: 1, height: 1, backgroundColor: '#e5e7eb' },
  seccioLabel: { fontSize: 11, fontWeight: 600, color: '#9ca3af', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em' },

  // ── Restriccions per domicili ─────────────────────────────────────────────────
  restrDomBloc:   { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px', marginBottom: 6 },
  restrDomHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer' },
  restrDomCodi:   { fontSize: 13, fontWeight: 700, color: '#007AFF' },
  restrDomToggle: { fontSize: 11, color: '#9ca3af' },
  restrGrid:      { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', alignItems: 'center', marginBottom: 8 },
  restrLabel:     { fontSize: 12, color: '#6b7280', margin: 0, whiteSpace: 'nowrap' },
  restrInput:     { border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 8px', fontSize: 13, backgroundColor: '#fff', width: '100%', boxSizing: 'border-box' },

  // ── Restricció hospital ───────────────────────────────────────────────────────
  restrHospBloc:   { borderTop: '1px solid #f3f4f6', paddingTop: 8, marginTop: 4 },
  restrHospWarn:   { color: '#f59e0b', fontSize: 11 },
  restrHospOpcions:{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 },
  restrHospOpcio:  { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', cursor: 'pointer' },

  // ── Seqüència optimitzada ──────────────────────────────────────────────────
  sequenciaBloc: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 10px', marginBottom: 8 },
  seqDom:        { fontSize: 12, fontWeight: 600, color: '#166534', display: 'flex', alignItems: 'center', gap: 3 },
  seqHospital:   { fontSize: 12, fontWeight: 700, color: '#b45309', backgroundColor: '#fef3c7', padding: '2px 6px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 3 },
  seqInici:      { fontSize: 12, fontWeight: 600, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 3 },
  seqArrow:      { fontSize: 11, color: '#9ca3af', margin: '0 2px' },

  // ── Ordre parcial ─────────────────────────────────────────────────────────────
  ordreFila:   { display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '7px 10px', marginBottom: 4 },
  ordreCodi:   { fontSize: 13, fontWeight: 600, color: '#0369a1' },
  ordreArrow:  { fontSize: 11, color: '#6b7280', flex: 1, textAlign: 'center' },
  ordreDelBtn: { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', fontSize: 14, lineHeight: 1 },
  ordreAfegir: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  ordreSelect: { flex: 1, minWidth: 80, border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', fontSize: 13, backgroundColor: '#fafafa' },
  ordreAddBtn: { backgroundColor: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },

  // ── Previsualització simulació ─────────────────────────────────────────────
  simulacioBloc:    { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px', marginTop: 12 },
  simulacioHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  simulacioTitol:   { fontSize: 13, fontWeight: 700, color: '#166534' },
  simulacioClose:   { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0 4px', fontSize: 14 },
  simulacioStats:   { display: 'flex', gap: 20 },
  simulacioStat:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 },
  simulacioStatVal: { fontSize: 20, fontWeight: 700, color: '#166534', lineHeight: 1 },
  simulacioStatLabel: { fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },

  // ── Botons d'acció ─────────────────────────────────────────────────────────
  botomsAccio:          { display: 'flex', gap: 8, marginTop: 14 },
  btnSimular:           { flex: 1, backgroundColor: '#f0f9ff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 8px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnOptimitzarIGuardar:{ flex: 2, backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 8px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },

  toast: { position: 'fixed', bottom: 72, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#111827', color: '#fff', padding: '8px 18px', borderRadius: 8, fontSize: 14, zIndex: 9999, whiteSpace: 'nowrap' },

  // ── Secció rutes finalitzades ─────────────────────────────────────────────
  seccioFinalitzades: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px', cursor: 'pointer', marginTop: 4 },
  badgeCount:         { backgroundColor: '#e5e7eb', color: '#374151', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999 },
  btnNetejar:         { backgroundColor: '#fff5f5', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' },

  // ── Reinici d'execució ────────────────────────────────────────────────────────
  reiniciarBloc:   { backgroundColor: '#FFF8F0', border: '1px solid #FED7AA', borderRadius: 10, padding: '12px 14px', marginBottom: 14 },
  reiniciarInfo:   { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  reiniciarTitol:  { margin: 0, fontSize: 13, fontWeight: 700, color: '#92400E' },
  reiniciarDesc:   { margin: '2px 0 0', fontSize: 11, color: '#B45309' },
  btnReiniciar:    { width: '100%', backgroundColor: '#fff', color: '#B45309', border: '1px solid #FED7AA', borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxSizing: 'border-box' },

  // ── Detall de ruta ────────────────────────────────────────────────────────────
  detallDomFila:    { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 0', borderBottom: '1px solid #f3f4f6' },
  detallDomNum:     { width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 1 },
  detallBadgeIngres:{ display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: '#d1fae5', color: '#065f46' },
  detallBadgeAlta:  { display: 'inline-block', marginTop: 3, padding: '2px 7px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: '#fee2e2', color: '#991b1b' },
}
